import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  EARNINGS_RANGE_DAYS,
  EARNINGS_RANGE_LABELS,
  EARNINGS_RANGES,
  EARNINGS_SERIES_MAX_DAYS,
  addDays,
  daysInclusive,
  formatCents,
  utcDay,
  type AdminReportsResponse,
  type ClipCounts,
  type EarningsRange,
  type ReportsCampaignRow,
} from '@nexus/shared';

import { ApiError, apiGet } from '../../lib/api';
import {
  Alert,
  AreaChart,
  Card,
  DataTable,
  EmptyState,
  LoadErrorPage,
  LoadingPage,
  Money,
  Num,
  PageHeader,
  Pill,
  Pills,
  Stat,
  StatGrid,
  StatusPill,
  TextField,
  formatMonth,
  formatNumber,
  formatWhen,
  type ChartPoint,
  type Column,
} from '../../ui';

/**
 * Reports: the whole operation, over a range.
 *
 * The operator asked for it in one sentence — "how many total clips have been
 * submitted across all campaigns, and how much money has been paid out and come
 * in" — and it is the one screen this app did not have. Every other operator
 * page owns one job and its own numbers; nothing answered "how are we doing".
 *
 * ## Flows are in the range. Balances are not.
 *
 * "How much came in last month" has an answer. "How much was owed last month"
 * does not — owed is a state, not an event — so the two blocks are separated
 * and labelled, rather than putting a range control over figures the range
 * cannot change. Getting this wrong is the commonest way a dashboard lies.
 *
 * ## Every money figure is a sum of ledger entries
 *
 * Not one of them reads a stored balance, and the page prints the proof: the
 * reconciliation line is sources minus uses over ALL TIME, which is the trial
 * balance rearranged. If it is ever not zero, this page says so in red before
 * anybody is paid wrongly — which is exactly what an operator wants from the
 * screen they check in the morning.
 *
 * ## Two charts, because two units
 *
 * Clips and dollars do not share an axis. A single chart with a count on one
 * series and cents on the other would be drawing a bar twice the height of
 * another bar that is worth a hundredth of it. So: one chart of clips, one of
 * money, both with the same range control above them.
 */
export function AdminReports(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const { range, from, to } = readRange(params);

  const [data, setData] = useState<AdminReportsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* The query the API is asked for, and the reason it is refused when it is.
     Identical to the Wallet's, and deliberately: same constant, same cap, same
     sentence, because two range controls that drifted apart would be two
     different answers to "what does 90 days mean". */
  const request = useMemo<{ search: string; refusal: string | null }>(() => {
    if (range === 'all') return { search: '', refusal: null };
    if (range === 'custom') {
      if (!isDay(from) || !isDay(to)) {
        return { search: '', refusal: 'Pick both dates to see a custom range.' };
      }
      if (to < from) return { search: '', refusal: 'The end of the range is before its start.' };
      const days = daysInclusive(from, to);
      if (days > EARNINGS_SERIES_MAX_DAYS) {
        return {
          search: '',
          refusal: `A custom range covers at most ${EARNINGS_SERIES_MAX_DAYS} days, and that one is ${days.toLocaleString('en-US')}. Pick a shorter one, or use All time.`,
        };
      }
      return { search: `?from=${from}&to=${to}`, refusal: null };
    }
    const today = utcDay(new Date());
    const start = addDays(today, -(EARNINGS_RANGE_DAYS[range] - 1));
    return { search: `?from=${start}&to=${today}`, refusal: null };
  }, [range, from, to]);

  useEffect(() => {
    if (request.refusal !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiGet<AdminReportsResponse>(`/admin/reports${request.search}`);
        if (!cancelled) {
          setData(body);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load the reports.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request]);

  function setRange(value: EarningsRange): void {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('range', value);
      if (value !== 'custom') {
        next.delete('from');
        next.delete('to');
      }
      return next;
    });
  }

  function setDay(key: 'from' | 'to', value: string): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set('range', 'custom');
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      /* `replace`, because dragging a date picker is not navigation. */
      { replace: true },
    );
  }

  if (error && data === null) {
    return (
      <LoadErrorPage
        eyebrow="Operator"
        title="Reports"
        heading="Could not load the reports."
        onRetry={() => setParams(new URLSearchParams(params))}
      >
        <span>{error}</span>
      </LoadErrorPage>
    );
  }

  const customDays = range === 'custom' && isDay(from) && isDay(to);
  const rangeName = customDays
    ? `${formatWhen(`${from}T00:00:00.000Z`)} to ${formatWhen(`${to}T00:00:00.000Z`)}`
    : EARNINGS_RANGE_LABELS[range];
  const rangeSentence = customDays ? rangeName : rangeName.toLowerCase();

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Reports"
        lead="Every campaign, over a range you choose. Every money figure is a sum of ledger entries — nothing here is a stored balance."
      />

      <Card tight>
        <div className="range-controls">
          <Pills label="Range">
            {EARNINGS_RANGES.map((value) => (
              <Pill key={value} selected={range === value} onClick={() => setRange(value)}>
                {EARNINGS_RANGE_LABELS[value]}
              </Pill>
            ))}
          </Pills>

          {range === 'custom' && (
            <div className="range-dates">
              <TextField
                label="From"
                type="date"
                value={from}
                max={to || undefined}
                onChange={(event) => setDay('from', event.target.value)}
              />
              <TextField
                label="To"
                type="date"
                value={to}
                min={from || undefined}
                onChange={(event) => setDay('to', event.target.value)}
              />
            </div>
          )}
        </div>
      </Card>

      {request.refusal !== null ? (
        <Alert tone="waiting">{request.refusal}</Alert>
      ) : error ? (
        <Alert tone="critical">{error}</Alert>
      ) : data === null ? (
        <LoadingPage eyebrow="Operator" title="Reports" stats />
      ) : (
        <Report data={data} rangeSentence={rangeSentence} rangeName={rangeName} />
      )}
    </>
  );
}

const RANGE_VALUES: readonly string[] = EARNINGS_RANGES;

/** What the URL says, defended against anything a person can type into it. */
function readRange(params: URLSearchParams): {
  range: EarningsRange;
  from: string;
  to: string;
} {
  const raw = params.get('range') ?? '';
  const range = (RANGE_VALUES.includes(raw) ? raw : '30d') as EarningsRange;
  return { range, from: params.get('from') ?? '', to: params.get('to') ?? '' };
}

/** `YYYY-MM-DD` only. Anything else is treated as not given. */
const isDay = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

/* ------------------------------------------------------------------ */
/* The report itself                                                   */
/* ------------------------------------------------------------------ */

function Report({
  data,
  rangeSentence,
  rangeName,
}: {
  data: AdminReportsResponse;
  rangeSentence: string;
  rangeName: string;
}): React.JSX.Element {
  const label = (date: string): string =>
    data.bucket === 'month'
      ? formatMonth(`${date}T00:00:00.000Z`)
      : formatWhen(`${date}T00:00:00.000Z`);

  const clipPoints: ChartPoint[] = data.points.map((point) => ({
    key: point.date,
    label: label(point.date),
    value: point.submissions,
    compare: point.approvedSubmissions,
  }));
  const moneyPoints: ChartPoint[] = data.points.map((point) => ({
    key: point.date,
    label: label(point.date),
    value: point.moneyInCents,
    compare: point.moneyOutCents,
  }));

  const bucketWord = data.bucket === 'month' ? 'months' : 'days';

  return (
    <>
      {!data.reconciliation.balanced && (
        <Alert tone="critical" title="The books do not balance">
          <span>
            Sources ({formatCents(data.reconciliation.sourcesCents)}) minus uses (
            {formatCents(data.reconciliation.usesCents)}) is{' '}
            {formatCents(data.reconciliation.deltaCents)}, and it has to be $0.00. Every figure
            below is a sum of ledger entries, so this is a real difference in the books and not a
            rounding artefact.
          </span>
          <span>
            <Link to="/admin/ledger">Open the trial balance</Link> — it names the transactions whose
            legs do not sum.
          </span>
        </Alert>
      )}

      <Card
        title="What happened"
        sub={`Clips and creators over ${rangeSentence}. A clip is counted on the day it was submitted.`}
      >
        <StatGrid dense>
          {/* The four status figures are tiles of their own AND the note under
              the total used to repeat them word for word, which is the same
              four numbers printed twice across one row. The headline now says
              something the tiles cannot, and each tile says why it is worth its
              own height. */}
          <Stat
            label="Clips submitted"
            value={<Num value={data.totals.clips.total} />}
            note="Counted on the day each one came in."
            emphasis={data.totals.clips.total > 0}
          />
          <Stat
            label="In review"
            value={<Num value={data.totals.clips.inReview} />}
            note={data.totals.clips.inReview > 0 ? 'Waiting on you.' : undefined}
            emphasis={data.totals.clips.inReview > 0}
          />
          <Stat label="Approved" value={<Num value={data.totals.clips.approved} />} />
          <Stat label="Rejected" value={<Num value={data.totals.clips.rejected} />} />
          <Stat
            label="Held"
            value={<Num value={data.totals.clips.held} />}
            note={data.totals.clips.held > 0 ? 'A fraud rule is holding the money.' : undefined}
          />
          <Stat
            label="Creators posting"
            value={<Num value={data.totals.creatorsActive} />}
            note={`${formatNumber(data.totals.creatorsJoined)} joined a campaign in this range.`}
          />
          <Stat
            label="Verified views"
            value={<Num value={data.totals.verifiedViews} />}
            note="Windows that closed in this range."
          />
        </StatGrid>
      </Card>

      <Card
        title="Money in and out"
        sub={`Over ${rangeSentence}, from the ledger. Money in is client funding recorded; money out is payouts actually paid.`}
      >
        <StatGrid dense>
          <Stat
            label="Money in"
            value={<Money cents={data.totals.moneyInCents} />}
            note="Client funding recorded."
            emphasis={data.totals.moneyInCents > 0}
          />
          <Stat
            label="Money out"
            value={<Money cents={data.totals.moneyOutCents} />}
            note="Paid to creators."
            emphasis={data.totals.moneyOutCents > 0}
          />
          <Stat
            label="Earned by creators"
            value={<Money cents={data.totals.creatorEarningsCents} />}
            /* ON CAMPAIGNS. A referral bonus is money a creator earned too, and
               it is the tile two along, out of a different pocket — counting it
               in both would be the same money twice and would stop the campaign
               table below adding up to this figure. */
            note="On campaigns, net of reversals — not yet paid. Referral bonuses are counted separately."
          />
          <Stat
            label="Margin kept"
            value={<Money cents={data.totals.marginCents} />}
            note="Funding fees plus the per-earning margin."
          />
          <Stat
            label="Referral bonuses"
            value={<Money cents={data.totals.referralBonusCents} />}
            note="Accrued out of WeClip's own pot."
          />
        </StatGrid>
      </Card>

      <Card
        title="Where the money is right now"
        sub="Balances, not flows: these are what is true today and the range above does not change them."
      >
        <StatGrid dense>
          <Stat
            label="Owed to creators"
            value={<Money cents={data.balances.owedCents} />}
            note="Earned and not yet dispatched."
            emphasis={data.balances.owedCents > 0}
          />
          <Stat
            label="In flight"
            value={<Money cents={data.balances.inTransitCents} />}
            note="Approved and on its way out."
          />
          <Stat
            label="Budget left"
            value={<Money cents={data.balances.budgetRemainingCents} />}
            note="Across every campaign's funds account."
          />
          <Stat label="Margin kept, all time" value={<Money cents={data.balances.marginKeptCents} />} />
          <Stat label="Funded, all time" value={<Money cents={data.balances.fundedCents} />} />
          <Stat label="Paid out, all time" value={<Money cents={data.balances.paidOutCents} />} />
        </StatGrid>

        <p className="muted">
          {/* The proof, printed rather than assumed. Over all time, what came in
              plus what WeClip put in from its own pot equals what is left in the
              campaigns, plus what was kept, plus what is owed, plus what is in
              flight, plus what has gone. That identity IS the trial balance. */}
          {formatCents(data.balances.fundedCents)} funded and{' '}
          {formatCents(data.balances.referralBonusCents)} from the referral pot, against{' '}
          {formatCents(data.balances.budgetRemainingCents)} left,{' '}
          {formatCents(data.balances.marginKeptCents)} kept,{' '}
          {formatCents(data.balances.owedCents)} owed,{' '}
          {formatCents(data.balances.inTransitCents)} in flight and{' '}
          {formatCents(data.balances.paidOutCents)} paid out —{' '}
          {data.reconciliation.balanced
            ? 'which comes out at $0.00, so the books balance.'
            : `which is out by ${formatCents(data.reconciliation.deltaCents)}.`}
        </p>
      </Card>

      <Card
        title="Clips over time"
        sub="Submitted, and how many of them are approved. Both series are bucketed on the day the clip came in."
        tight
      >
        <AreaChart
          points={clipPoints}
          valueName="submitted"
          compareName="approved"
          formatValue={(n) => formatNumber(n)}
          summary={`Clips over ${rangeName}: ${data.totals.clips.total} submitted and ${data.totals.clips.approved} approved across ${data.points.length} ${bucketWord}, from ${data.from} to ${data.to}.`}
          emptyNote="No clips came in over this range."
        />
      </Card>

      <Card
        title="Money over time"
        sub="Client funding recorded, and payouts paid, on the day the ledger recorded each one."
        tight
      >
        <AreaChart
          points={moneyPoints}
          valueName="in"
          compareName="out"
          formatValue={(cents) => formatCents(cents)}
          summary={`Money over ${rangeName}: ${formatCents(data.totals.moneyInCents)} in and ${formatCents(data.totals.moneyOutCents)} out across ${data.points.length} ${bucketWord}, from ${data.from} to ${data.to}.`}
          emptyNote="No money moved over this range."
        />
      </Card>

      <CampaignTable data={data} rangeSentence={rangeSentence} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Every campaign, in one table                                        */
/* ------------------------------------------------------------------ */

function CampaignTable({
  data,
  rangeSentence,
}: {
  data: AdminReportsResponse;
  rangeSentence: string;
}): React.JSX.Element {
  const columns: Array<Column<ReportsCampaignRow>> = [
    {
      key: 'name',
      header: 'Campaign',
      primary: true,
      cell: (row) => (
        <>
          <Link to={`/admin/campaigns/${row.slug}`}>{row.name}</Link>
          <span className="cell-note">{row.brandName}</span>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <StatusPill status={row.status} domain="campaign" />,
    },
    {
      key: 'joined',
      header: 'Joined',
      numeric: true,
      align: 'end',
      cell: (row) => (
        <>
          <Num value={row.creatorsJoined} />
          <span className="cell-note">{row.creatorsActive} posting</span>
        </>
      ),
    },
    {
      key: 'clips',
      header: 'Clips',
      numeric: true,
      align: 'end',
      cell: (row) => (
        <>
          <Num value={row.clips.total} />
          <span className="cell-note">{clipBreakdown(row.clips)}</span>
        </>
      ),
    },
    {
      key: 'views',
      header: 'Views',
      numeric: true,
      align: 'end',
      cell: (row) => <Num value={row.verifiedViews} />,
    },
    {
      key: 'spend',
      header: 'Spend',
      numeric: true,
      align: 'end',
      cell: (row) => (
        <>
          <Money cents={row.spendCents} />
          <span className="cell-note">{formatCents(row.marginCents)} kept</span>
        </>
      ),
    },
    {
      key: 'left',
      header: 'Budget left',
      numeric: true,
      align: 'end',
      /* Money in is the NOTE rather than a column of its own: eight columns did
         not fit a 1366px content area, and "what is left" is the figure an
         operator acts on while "what came in" is the context for it. The CSV
         below carries both as their own fields. */
      cell: (row) => (
        <>
          <Money cents={row.budgetRemainingCents} />
          <span className="cell-note">of {formatCents(row.fundedCents)} in</span>
        </>
      ),
    },
  ];

  /* The CSV is built from the ROWS ON SCREEN, not from a second request: a
     download that disagreed with the table it came from is worse than no
     download. A `data:` URL, so there is no object URL to leak. */
  const csv = useMemo(() => {
    const header = [
      'campaign',
      'brand',
      'status',
      'creators_joined',
      'creators_posting',
      'clips_total',
      'clips_in_review',
      'clips_approved',
      'clips_rejected',
      'clips_held',
      'verified_views',
      'money_in',
      'creator_spend',
      'margin_kept',
      'spend',
      'budget_left',
    ].join(',');
    const lines = data.campaigns.map((row) =>
      [
        csvCell(row.name),
        csvCell(row.brandName),
        row.status,
        row.creatorsJoined,
        row.creatorsActive,
        row.clips.total,
        row.clips.inReview,
        row.clips.approved,
        row.clips.rejected,
        row.clips.held,
        row.verifiedViews,
        dollars(row.fundedCents),
        dollars(row.creatorSpendCents),
        dollars(row.marginCents),
        dollars(row.spendCents),
        dollars(row.budgetRemainingCents),
      ].join(','),
    );
    return `data:text/csv;charset=utf-8,${encodeURIComponent([header, ...lines].join('\n'))}`;
  }, [data.campaigns]);

  return (
    <Card
      title="Every campaign"
      /* WHICH COLUMNS THE RANGE OWNS, said on the card rather than left to be
         discovered. Every figure here is inside the range and the columns add
         up to the totals above — except Budget left, which is a balance and
         cannot have a range-shaped answer. */
      sub={`Clips, creators, views and money over ${rangeSentence}; these columns add up to the totals above. Budget left is a balance — what is in each campaign's funds account today. Joined is the participation rows and verified views is the client export's own predicate, exactly as the campaign screens count them.`}
    >
      <DataTable
        columns={columns}
        rows={data.campaigns}
        getKey={(row) => row.campaignId}
        empty={
          <EmptyState title="No campaigns yet.">
            A campaign needs a client, a rate and a budget before any of these numbers exist.
          </EmptyState>
        }
      />
      <div className="actions">
        <a
          className="btn btn-quiet small"
          href={csv}
          download={`nexus-campaigns-${data.from}-to-${data.to}.csv`}
        >
          Download CSV
        </a>
      </div>
    </Card>
  );
}

/** Integer cents as a plain decimal, for a spreadsheet. */
const dollars = (cents: number): string => (cents / 100).toFixed(2);

/** A campaign name can contain a comma; a CSV cell cannot, unquoted. */
function csvCell(text: string): string {
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * "3 in review · 1 rejected", and nothing at all when there is nothing to say.
 *
 * The parts that are ZERO are left out: a note reading "0 in review · 0
 * rejected · 0 held" on every row is four words of noise per campaign, and the
 * total is already in the column above it.
 */
function clipBreakdown(clips: ClipCounts): string {
  const parts: string[] = [];
  if (clips.inReview > 0) parts.push(`${clips.inReview} in review`);
  if (clips.approved > 0) parts.push(`${clips.approved} approved`);
  if (clips.rejected > 0) parts.push(`${clips.rejected} rejected`);
  if (clips.held > 0) parts.push(`${clips.held} held`);
  return parts.join(' · ');
}
